export function navigate(to: string, options: { replace?: boolean; state?: Record<string, unknown> } = {}): void {
  if (options.replace) window.history.replaceState(options.state ?? {}, '', to);
  else window.history.pushState(options.state ?? {}, '', to);
  window.dispatchEvent(new PopStateEvent('popstate'));
  window.scrollTo({ top: 0, behavior: 'instant' });
}

interface NamedRouteRecord {
  id: string;
  name: string;
}

export function nameSlug(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '') || 'Untitled';
}

export function profilePath(profile: NamedRouteRecord): string {
  return `/profiles/${encodeURIComponent(profile.id)}/${nameSlug(profile.name)}`;
}

export function packPath(pack: NamedRouteRecord): string {
  return `/packs/${encodeURIComponent(pack.id)}/${nameSlug(pack.name)}`;
}

export function absoluteProfileUrl(profile: NamedRouteRecord): string {
  return new URL(profilePath(profile), window.location.origin).toString();
}

export function canonicalizeCurrentPath(path: string): void {
  if (window.location.pathname === path) return;
  window.history.replaceState(window.history.state, '', `${path}${window.location.search}${window.location.hash}`);
}

export async function copyText(value: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.append(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('Copy was unavailable.');
}

export function formatDate(value: string): string {
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(value));
}

export function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}
